import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    LogOut, Plus, Trash2, ImagePlus, Film, Clock3, ExternalLink, AlertTriangle, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import SiteNav from "@/components/SiteNav";
import { CoverCropModal, GalleryModal, VideoUploadModal, DraftPreviewLink } from "@/pages/studio/CaseStudyMedia";
import FullProjectEditor from "@/pages/studio/FullProjectEditor";
import { labelFor as categoryLabelFor } from "@/lib/projectCategories";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
    getStudioPassword, setStudioPassword, clearStudioPassword, studioHeaders, isStudioAuthed,
} from "@/lib/studioAuth";
import PackagesTab from "@/pages/PackagesTab";
import ShootOrdersTab from "@/pages/studio/ShootOrdersTab";
import QuotationsPanel from "@/pages/studio/QuotationsPanel";
import DuplicateFinder from "@/pages/studio/crm/DuplicateFinder";
import KitContentTab from "@/pages/studio/KitContentTab";
import { SalesBrainTab } from "@/pages/studio/SalesBrain";
import SalesCRM from "@/pages/studio/crm/SalesCRM";
import SalesInbox from "@/pages/studio/crm/SalesInbox";
import BrainHub from "@/pages/studio/crm/BrainHub";
import ProposalLibrary from "@/pages/studio/crm/ProposalLibrary";
import ProjectsOps from "@/pages/studio/projects/ProjectsOps";
import LandingStudio from "@/pages/studio/landing/LandingStudio";
import SystemVersionEditor from "@/pages/studio/SystemVersionEditor";
import SubscribersTab from "@/pages/studio/SubscribersTab";
import DigestsTab from "@/pages/studio/DigestsTab";
import OverviewTab from "@/pages/studio/OverviewTab";
import AdminToolsTab from "@/pages/studio/AdminToolsTab";
import WebsiteContentTab from "@/pages/studio/WebsiteContent";
import { MoreActions } from "@/components/MoreActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Brand Film", "Campaign", "Product Video", "Social Reels", "Food Photography"];
const SERVICE_OPTIONS = [
    "Creative Direction",
    "Brand Film Production",
    "Campaign Production",
    "Product Video Production",
    "Social Reels Production",
    "Food Photography",
    "Menu Photography",
];

// ---------- Login gate ----------
function StudioLogin({ onAuthed }) {
    const [pwd, setPwd] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!pwd.trim()) return;
        setLoading(true);
        try {
            await axios.post(`${API}/studio/auth`, { password: pwd.trim() });
            setStudioPassword(pwd.trim());
            toast.success("Welcome back, Kaily");
            onAuthed();
        } catch (err) {
            // More specific error so Kaily can self-diagnose: wrong password vs
            // network blocked (ad-blocker, corporate proxy, captive wifi). The
            // generic "Wrong password" used to confuse her when the real cause
            // was uBlock blocking *.emergent.host endpoints.
            const status = err?.response?.status;
            if (status === 401) {
                toast.error("Wrong password", {
                    description: "Check caps-lock — password is all lowercase.",
                });
            } else if (status) {
                toast.error(`Server error (HTTP ${status})`, {
                    description: err?.response?.data?.detail || "Please retry, or contact support.",
                });
            } else {
                toast.error("Network blocked", {
                    description: "Browser couldn't reach our auth server. Disable any ad-blocker for this site, or try incognito mode.",
                    duration: 8000,
                });
                console.error("[studioAuth] network error:", err?.message, err?.code);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso flex flex-col">
            <SiteNav />
            <main className="flex-1 flex items-center justify-center px-6">
                <form
                    onSubmit={submit}
                    data-testid="studio-login-form"
                    className="w-full max-w-md bg-cafe-latte border border-border rounded-[2rem] p-8 md:p-10"
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4">Studio · private</div>
                    <h1 className="font-serif text-4xl md:text-5xl text-cafe-espresso leading-tight">
                        Welcome back.
                    </h1>
                    <p className="mt-3 text-cafe-mocha">Enter the studio password to continue.</p>
                    <div className="mt-6">
                        <Label htmlFor="studio-pwd" className="text-cafe-espresso font-medium">Password</Label>
                        <Input
                            id="studio-pwd"
                            data-testid="studio-pwd"
                            type="password"
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            placeholder="••••••••"
                            className="mt-2 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="studio-login-btn"
                        className="mt-8 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8 w-full"
                    >
                        {loading ? "Checking…" : "Unlock studio"}
                    </Button>
                    <Link to="/" className="mt-6 block text-center text-sm text-cafe-mocha hover:text-cafe-olive">
                        ← Back to site
                    </Link>
                </form>
            </main>
            <SiteFooter />
        </div>
    );
}

// ---------- Studio dashboard ----------
export default function StudioPage() {
    const [authed, setAuthed] = useState(isStudioAuthed());

    // re-verify password on mount (in case it was revoked)
    useEffect(() => {
        if (!authed) return;
        (async () => {
            try {
                await axios.get(`${API}/studio/session`);
            } catch {
                clearStudioPassword();
                setAuthed(false);
            }
        })();
    }, [authed]);

    if (!authed) return <StudioLogin onAuthed={() => setAuthed(true)} />;

    return <StudioDashboard onLogout={() => { clearStudioPassword(); setAuthed(false); }} />;
}

function StudioDashboard({ onLogout }) {
    // ── Phase 1 restructure: 6 primary workspaces (was 12 scrolling tabs) ──
    const [searchParams, setSearchParams] = useSearchParams();
    const NAV = [
        { id: "overview", label: "Overview", subs: [] },
        { id: "sales", label: "Leads & Sales", subs: [["inbox", "Inbox"], ["crm", "Sales Pipeline"], ["brain", "AI Sales Brain"], ["library", "Proposal & Quotation Library"], ["quotes", "Quotation Requests"], ["duplicates", "Duplicate Finder"]] },
        { id: "clients", label: "Client Projects", subs: [["projects", "Projects"], ["landing-pages", "Landing Pages"]] },
        { id: "site", label: "Portfolio & Website", subs: [["website", "Website Content"], ["projects", "Case Studies"], ["articles", "Journal"], ["instagram", "Instagram Posts"]] },
        { id: "settings", label: "Offers & Settings", subs: [["packages", "Offers & Pricing"], ["orders", "Digital Products · Orders"], ["subscribers", "Subscriptions"], ["system", "Monthly Editions"], ["digests", "Delivery Emails"], ["kit-content", "Content Library"], ["admin", "Admin Tools"], ["archive", "Archive (read-only)"]] },
    ];
    // legacy ?tab= deep-links (print pages, emails, bookmarks) keep working
    const LEGACY = {
        orders: ["settings", "orders"], "shoot-orders": ["sales", "quotes"], "sales-brain": ["sales", "brain"],
        subscribers: ["settings", "subscribers"], digests: ["settings", "digests"], system: ["settings", "system"],
        packages: ["settings", "packages"], "kit-content": ["settings", "kit-content"], projects: ["site", "projects"],
        "landing-pages": ["clients", "landing-pages"], products: ["settings", "orders"], bookings: ["settings", "archive"],
        articles: ["site", "articles"], journal: ["site", "articles"], inquiries: ["sales", "inbox"], instagram: ["site", "instagram"],
    };
    const rawTab = searchParams.get("tab") || "overview";
    const rawSub = searchParams.get("sub") || "";
    const initial = NAV.some((g) => g.id === rawTab) ? [rawTab, rawSub] : (LEGACY[rawTab] || ["overview", ""]);
    const [group, setGroupState] = useState(initial[0]);
    const [sub, setSubState] = useState(() => {
        const grp = NAV.find((g) => g.id === initial[0]);
        return (initial[1] && grp?.subs.some(([k]) => k === initial[1])) ? initial[1] : (grp?.subs[0]?.[0] || "");
    });
    const setNav = (g, s = "") => {
        const grp = NAV.find((x) => x.id === g);
        const nextSub = (s && grp?.subs.some(([k]) => k === s)) ? s : (grp?.subs[0]?.[0] || "");
        setGroupState(g);
        setSubState(nextSub);
        const next = new URLSearchParams(searchParams);
        if (g === "overview") { next.delete("tab"); next.delete("sub"); }
        else { next.set("tab", g); if (nextSub) next.set("sub", nextSub); else next.delete("sub"); }
        setSearchParams(next, { replace: true });
    };
    const activeGroup = NAV.find((g) => g.id === group);
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="studio-dashboard">
            <SiteNav />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                        <div>
                            <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-3">
                                Studio dashboard
                            </div>
                            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cafe-espresso leading-tight">
                                Manage your <em className="font-display italic">work</em>.
                            </h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={() => navigate("/os")}
                                data-testid="studio-os-btn"
                                className="rounded-full bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive h-10 px-5"
                            >
                                ✦ Kaily OS
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => navigate("/")}
                                className="rounded-full border-cafe-espresso/20 text-cafe-espresso bg-transparent hover:bg-cafe-latte h-10 px-5"
                            >
                                View site
                            </Button>
                            <Button
                                onClick={onLogout}
                                data-testid="studio-logout-btn"
                                variant="outline"
                                className="rounded-full border-cafe-espresso/20 text-cafe-espresso bg-transparent hover:bg-cafe-latte h-10 px-5"
                            >
                                <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} /> Log out
                            </Button>
                        </div>
                    </div>

                    {/* Primary workspaces — 6 items, no horizontal scroll */}
                    <div className="flex flex-wrap gap-1.5 mb-4" data-testid="studio-nav">
                        {NAV.map((g) => (
                            <button
                                key={g.id}
                                data-testid={`nav-${g.id}`}
                                onClick={() => setNav(g.id)}
                                className={cn(
                                    "px-4 py-2 rounded-full text-sm transition-colors",
                                    group === g.id
                                        ? "bg-cafe-espresso text-cafe-cream font-medium"
                                        : "text-cafe-mocha hover:text-cafe-espresso hover:bg-cafe-latte"
                                )}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>

                    {/* Secondary navigation inside grouped workspaces */}
                    {activeGroup?.subs.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-8 pb-3 border-b border-border" data-testid="studio-subnav">
                            {activeGroup.subs.map(([k, label]) => (
                                <button
                                    key={k}
                                    data-testid={`subnav-${k}`}
                                    onClick={() => setNav(group, k)}
                                    className={cn(
                                        "px-3.5 py-1.5 rounded-full text-xs uppercase tracking-[0.08em] font-semibold border transition-colors",
                                        sub === k
                                            ? "border-cafe-olive bg-cafe-olive/10 text-cafe-olive"
                                            : "border-border text-cafe-mocha hover:text-cafe-espresso"
                                    )}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    {activeGroup?.subs.length === 0 && <div className="mb-8 border-b border-border pb-3" />}

                    {group === "overview" && <OverviewTab setNav={setNav} />}
                    {group === "clients" && sub === "projects" && <ProjectsOps />}
                    {group === "clients" && sub === "landing-pages" && <LandingStudio />}
                    {group === "sales" && sub === "inbox" && <SalesInbox />}
                    {group === "sales" && sub === "crm" && <SalesCRM onOpenBrain={() => setNav("sales", "brain")} />}
                    {group === "sales" && sub === "brain" && <BrainHub />}
                    {group === "sales" && sub === "library" && <ProposalLibrary />}
                    {group === "sales" && sub === "quotes" && <QuotationsPanel />}
                    {group === "sales" && sub === "duplicates" && <DuplicateFinder />}
                    {group === "settings" && sub === "orders" && <OrdersTab />}
                    {group === "settings" && sub === "subscribers" && <SubscribersTab />}
                    {group === "settings" && sub === "system" && <SystemVersionEditor />}
                    {group === "settings" && sub === "digests" && <DigestsTab />}
                    {group === "settings" && sub === "kit-content" && <KitContentTab />}
                    {group === "settings" && sub === "archive" && <ArchiveTab />}
                    {group === "site" && sub === "website" && <WebsiteContentTab />}
                    {group === "site" && sub === "projects" && <ProjectsTab />}
                    {group === "site" && sub === "articles" && <ArticlesTab />}
                    {group === "site" && sub === "instagram" && <InstagramTab />}
                    {group === "settings" && sub === "packages" && <PackagesTab />}
                    {group === "settings" && sub === "admin" && <AdminToolsTab />}
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}

// ---------- Orders tab ----------
function formatCountdown(dueAt, status) {
    if (status === "delivered") return { label: "Delivered", tone: "ok" };
    if (!dueAt) return { label: "—", tone: "muted" };
    const ms = new Date(dueAt).getTime() - Date.now();
    const hours = ms / 3600000;
    if (hours < 0) return { label: `${Math.floor(-hours)}h overdue`, tone: "danger" };
    if (hours < 12) return { label: `${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m left`, tone: "warn" };
    return { label: `${Math.floor(hours)}h left`, tone: "muted" };
}

function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setTick] = useState(0);
    const [soraQueue, setSoraQueue] = useState(0);
    // Budget banner dismissal — persists in localStorage so once Kaily clicks
    // "Dismiss" after Support tops up the Universal Key, the stale warning
    // doesn't keep appearing every reload because historical orders still
    // carry the budget_exhausted flag from past failures.
    const [budgetDismissed, setBudgetDismissed] = useState(() => {
        try {
            return typeof window !== "undefined" && localStorage.getItem("slowcafe-budget-dismissed-v1") === "1";
        } catch {
            return false;
        }
    });
    const dismissBudget = () => {
        setBudgetDismissed(true);
        try { localStorage.setItem("slowcafe-budget-dismissed-v1", "1"); } catch { /* private mode */ }
    };
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/orders`, { headers: studioHeaders() });
            setOrders(data || []);
        } catch (err) {
            if (err?.response?.status === 401) { clearStudioPassword(); window.location.reload(); return; }
            toast.error("Failed to load orders");
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    /** Cheap GET (~100ms) — tells admin how many reels are waiting behind
     *  the active Sora job. Polled every 20s while the tab is open. */
    const loadQueueDepth = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/sora_queue`, { headers: studioHeaders() });
            setSoraQueue(typeof data?.queue_depth === "number" ? data.queue_depth : 0);
        } catch {
            // Silent — queue badge is non-critical and admin already sees errors elsewhere
        }
    };

    useEffect(() => { load(); }, []);
    // Re-render every 60s so countdowns update
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 60000);
        return () => clearInterval(id);
    }, []);
    // Poll Sora queue depth every 20s — short enough to feel live, long
    // enough to not spam the backend when nothing's running.
    useEffect(() => {
        loadQueueDepth();
        const id = setInterval(loadQueueDepth, 20000);
        return () => clearInterval(id);
    }, []);

    const sendLoginEmail = async (order) => {
        try {
            const { data } = await axios.post(`${API}/checkout/kit/orders/${order.id}/send-login-email`, {}, { headers: studioHeaders() });
            toast.success(`Login email sent to ${data.sent_to}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        }
    };

    const restartOrder = async (order) => {
        try {
            const { data } = await axios.post(`${API}/checkout/kit/orders/${order.id}/restart`, {}, { headers: studioHeaders() });
            toast.success(`Order restarted — email sent to ${data.sent_to}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Restart failed");
        }
    };


    const deliver = async (order) => {
        const url = window.prompt("Deliverable URL (link to the kit PDF / Notion / etc) — leave blank to just mark delivered:", "");
        if (url === null) return;
        try {
            await axios.post(`${API}/orders/${order.id}/deliver`, { deliverable_url: url || "" }, { headers: studioHeaders() });
            toast.success(url ? "Delivered + email sent" : "Marked delivered");
            load();
        } catch {
            toast.error("Failed to mark delivered");
        }
    };

    const remove = async (id) => {
        try {
            await axios.delete(`${API}/orders/${id}`, { headers: studioHeaders() });
            toast.success("Order deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="orders-tab">
            {/* Universal Key budget warning — surfaces if ANY recent order
                has a budget-exhausted flag, so Kaily knows BEFORE clicking
                generate that the next AI run will fail. Dismissable because
                once Emergent Support tops up the key, historical flags stay
                on past orders and the banner would otherwise be sticky-stale. */}
            {(() => {
                if (budgetDismissed) return null;
                const budgetTrouble = orders.some(o =>
                    /insufficient.balance|budget.exceeded/i.test(o?.ai_videos_error || "") ||
                    /insufficient.balance|budget.exceeded/i.test(o?.ai_images_error || "") ||
                    (o?.ai_videos || []).some(v => v?.budget_exhausted) ||
                    (o?.ai_images || []).some(i => i?.budget_exhausted)
                );
                if (!budgetTrouble) return null;
                return (
                    <div
                        className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
                        data-testid="universal-key-warning"
                    >
                        <span className="mt-0.5 text-amber-700 text-base">⚠</span>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-amber-900 mb-0.5">
                                Universal Key budget exhausted — AI generation will fail
                            </p>
                            <p className="text-xs text-amber-800/90 leading-snug">
                                One or more recent orders hit{" "}
                                <span className="font-mono">insufficient_balance</span>. Top up via{" "}
                                <span className="font-medium">Profile → Universal Key → Add Balance</span>{" "}
                                before triggering more image / reel generation jobs. Already-rendered media is safe.
                            </p>
                        </div>
                        <button
                            onClick={dismissBudget}
                            data-testid="dismiss-budget-warning"
                            title="Dismiss after topping up — flag stays sticky on old orders even after balance is restored"
                            className="shrink-0 text-amber-800 hover:text-amber-900 text-xs underline px-2 py-1"
                        >
                            Topped up · dismiss
                        </button>
                    </div>
                );
            })()}

            <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-sm text-cafe-mocha">
                        {loading ? "Loading…" : `${orders.length} order${orders.length === 1 ? "" : "s"}`}
                    </div>
                    {/* Sora 2 cross-order queue badge — only shows when a reel
                        from another order is actively running, so the admin
                        knows a Premium kit will be delayed instead of looking
                        stuck. queue_depth excludes the actively running job;
                        a value of 0 + active=1 still hides the badge so we
                        don't spam the UI during a normal single-order run. */}
                    {soraQueue > 0 && (
                        <span
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cafe-terracotta/15 text-cafe-terracotta border border-cafe-terracotta/30 text-[11px] font-semibold uppercase tracking-[0.15em]"
                            data-testid="sora-queue-badge"
                            title="Reels from other orders are waiting for the cinematic render lock. Each ~5 min."
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cafe-terracotta opacity-60" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-cafe-terracotta" />
                            </span>
                            Sora queue: {soraQueue} waiting
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={load}
                        variant="outline"
                        className="rounded-full border-cafe-espresso/20 text-cafe-espresso bg-transparent hover:bg-cafe-latte h-9 px-4 text-xs"
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {!loading && orders.length === 0 && (
                <div className="border border-dashed border-border rounded-3xl p-12 text-center">
                    <div className="font-serif text-2xl text-cafe-espresso">No orders yet</div>
                    <p className="text-cafe-mocha text-sm mt-2 max-w-md mx-auto">
                        When a Gumroad sale comes in, it shows up here with a 48h delivery countdown.
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {orders.map((o) => {
                    const cd = formatCountdown(o.due_at, o.status);
                    const toneClass = {
                        ok: "bg-cafe-olive text-cafe-cream",
                        warn: "bg-cafe-terracotta text-cafe-cream",
                        danger: "bg-red-700 text-cafe-cream",
                        muted: "bg-cafe-latte text-cafe-espresso",
                    }[cd.tone];
                    return (
                        <div
                            key={o.id}
                            data-testid={`order-card-${o.id}`}
                            className="bg-cafe-latte rounded-2xl border border-border p-5 md:p-6"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={cn("text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full", toneClass)}>
                                            {cd.label}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-cafe-mocha">
                                            {o.status}
                                        </span>
                                    </div>
                                    <div className="font-serif text-xl text-cafe-espresso">
                                        {o.customer_name || o.customer_email}
                                        {o.is_demo && <span className="ml-2 align-middle inline-block text-[9px] font-sans font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-cafe-terracotta/15 text-cafe-terracotta border border-cafe-terracotta/30" data-testid={`order-demo-badge-${o.id}`}>Demo</span>}
                                        {o.test_clone && <span className="ml-2 align-middle inline-block text-[9px] font-sans font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-cafe-mocha/15 text-cafe-mocha border border-cafe-mocha/30" data-testid={`order-test-badge-${o.id}`}>Test</span>}
                                        {!o.is_demo && !o.test_clone && <span className="ml-2 align-middle inline-block text-[9px] font-sans font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-cafe-olive/12 text-cafe-olive border border-cafe-olive/30">Live customer</span>}
                                    </div>
                                    <div className="text-sm text-cafe-mocha">{o.customer_email}</div>
                                    <div className="mt-2 text-xs text-cafe-mocha">
                                        {o.product_name}
                                        {o.price_cents ? ` · ${o.currency || "RM"} ${(o.price_cents/100).toFixed(2)}` : ""}
                                        {" · "}order {o.id.slice(0, 8)}
                                    </div>
                                    {o.ai_status === "ready" && (() => {
                                        // Render-completion badge — text always counts as 100% once ai_status is ready;
                                        // images/videos contribute proportionally if generated.
                                        const imgs = o.ai_images || [];
                                        const vids = o.ai_videos || [];
                                        const imgReady = imgs.filter(i => i?.status === "ready").length;
                                        const vidReady = vids.filter(v => v?.status === "ready").length;
                                        const imgTotal = imgs.length;
                                        const vidTotal = vids.length;
                                        // Percentages — guard divide-by-zero by treating "no media generated yet" as N/A
                                        const parts = [];
                                        parts.push({ key: "TXT", pct: 100 });
                                        if (imgTotal > 0) parts.push({ key: "IMG", pct: Math.round((imgReady / imgTotal) * 100), count: `${imgReady}/${imgTotal}` });
                                        if (vidTotal > 0) parts.push({ key: "VID", pct: Math.round((vidReady / vidTotal) * 100), count: `${vidReady}/${vidTotal}` });
                                        const overall = Math.round(parts.reduce((acc, p) => acc + p.pct, 0) / parts.length);
                                        const tone = overall >= 95 ? "bg-cafe-olive text-cafe-cream" : overall >= 60 ? "bg-amber-600 text-cafe-cream" : "bg-cafe-terracotta text-cafe-cream";
                                        return (
                                            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`completion-${o.id}`}>
                                                <span className={cn("text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full", tone)}>
                                                    AI {overall}%
                                                </span>
                                                {parts.map(p => (
                                                    <span
                                                        key={p.key}
                                                        title={p.count || ""}
                                                        className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-cafe-cream text-cafe-mocha border border-border/60"
                                                    >
                                                        {p.key} {p.count || `${p.pct}%`}
                                                    </span>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="flex flex-wrap gap-2 items-center w-full lg:w-auto lg:justify-end">
                                    {o.ai_status === "generating" && (
                                        <span className="text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full bg-cafe-terracotta text-cafe-cream animate-pulse inline-flex items-center gap-1.5">
                                            <Sparkles className="h-3 w-3" /> AI writing…
                                        </span>
                                    )}
                                    {o.ai_status === "ready" && o.status !== "delivered" && (
                                        <Button
                                            onClick={() => navigate(`/studio/orders/${o.id}`)}
                                            data-testid={`review-${o.id}`}
                                            className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-4 text-xs"
                                        >
                                            <Sparkles className="h-3.5 w-3.5 mr-1" /> Review draft
                                        </Button>
                                    )}
                                    {o.ai_status === "failed" && (
                                        <Button
                                            onClick={() => navigate(`/studio/orders/${o.id}`)}
                                            variant="outline"
                                            className="rounded-full border-red-300 text-red-700 bg-transparent hover:bg-red-50 h-9 px-4 text-xs"
                                        >
                                            <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Retry AI
                                        </Button>
                                    )}
                                    {o.status === "delivered" && (
                                        <>
                                            <Button
                                                onClick={() => navigate(`/studio/orders/${o.id}`)}
                                                data-testid={`open-editor-${o.id}`}
                                                variant="outline"
                                                title="Open the order editor — generate images/videos, refine kit, save changes"
                                                className="rounded-full border-cafe-olive/40 text-cafe-olive bg-transparent hover:bg-cafe-olive/10 h-9 px-4 text-xs"
                                            >
                                                <Sparkles className="h-3.5 w-3.5 mr-1" /> Open editor
                                            </Button>
                                            <a
                                                href={o.deliverable_url || `/kit/${o.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="rounded-full bg-cafe-olive/10 text-cafe-olive border border-cafe-olive/30 h-9 px-4 text-xs inline-flex items-center font-medium"
                                            >
                                                <ExternalLink className="h-3.5 w-3.5 mr-1" /> View kit
                                            </a>
                                        </>
                                    )}
                                    {o.status !== "delivered" && o.intake && o.ai_status !== "generating" && o.ai_status !== "ready" && o.ai_status !== "failed" && (
                                        <Button
                                            onClick={() => navigate(`/studio/orders/${o.id}`)}
                                            data-testid={`open-editor-${o.id}`}
                                            className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-4 text-xs"
                                        >
                                            Open editor
                                        </Button>
                                    )}
                                    <MoreActions
                                        testid={`more-actions-${o.id}`}
                                        subject={`${o.customer_name || o.customer_email} · order #${String(o.id).slice(0, 8).toUpperCase()}`}
                                        items={[
                                            o.status !== "delivered" && {
                                                label: "Manual deliver", testid: `deliver-${o.id}`,
                                                confirm: "Deliver without using the AI draft (paste a manual link). The customer receives the delivery email.",
                                                onClick: () => deliver(o),
                                            },
                                            {
                                                label: "Send login email", testid: `send-login-${o.id}`,
                                                confirm: "Sends the customer a set-password / member login email.",
                                                onClick: () => sendLoginEmail(o),
                                            },
                                            {
                                                label: "Restart order", danger: true, testid: `restart-order-${o.id}`,
                                                confirm: "Status resets to awaiting_brief and the customer gets the brief + login email again. Generated content stays until re-generated.",
                                                confirmLabel: "Restart",
                                                onClick: () => restartOrder(o),
                                            },
                                            {
                                                label: "Delete order", danger: true, testid: `delete-order-${o.id}`,
                                                confirm: "Permanently deletes this order record. This cannot be undone.",
                                                confirmLabel: "Delete permanently",
                                                onClick: () => remove(o.id),
                                            },
                                        ]}
                                    />
                                </div>
                            </div>

                            {o.intake ? (
                                <div className="mt-4 pt-4 border-t border-border/60 text-sm">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2">Brief</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-cafe-espresso">
                                        <div><strong>Café:</strong> {o.intake.cafe_name} (@{o.intake.ig_handle})</div>
                                        <div><strong>Location:</strong> {o.intake.location}</div>
                                        <div><strong>Type:</strong> {(o.intake.cafe_type || []).join(", ") || "—"}</div>
                                        <div><strong>Customer:</strong> {(o.intake.customer_profile || []).join(", ") || "—"}</div>
                                        <div><strong>One word:</strong> {o.intake.one_word}</div>
                                        <div><strong>Refs:</strong> {o.intake.tone_refs || "—"}</div>
                                        <div className="md:col-span-2"><strong>Top 5 menu:</strong> {o.intake.top_menu}</div>
                                        {o.intake.avoid && <div className="md:col-span-2"><strong>Avoid:</strong> {o.intake.avoid}</div>}
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-4 pt-4 border-t border-border/60 text-xs text-cafe-mocha flex items-center gap-2">
                                    <Clock3 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    Customer hasn't submitted brief yet — 48h clock starts when they do.
                                </div>
                            )}

                            {o.deliverable_url && (
                                <div className="mt-3 pt-3 border-t border-border/60 text-xs">
                                    <a href={o.deliverable_url} target="_blank" rel="noreferrer" className="text-cafe-olive hover:text-cafe-oliveDark inline-flex items-center gap-1">
                                        <ExternalLink className="h-3.5 w-3.5" /> Delivered → {o.deliverable_url.replace(/^https?:\/\//, '').slice(0, 60)}
                                    </a>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


// ---------- Projects tab ----------
function ProjectsTab() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editorSlug, setEditorSlug] = useState(null);   // 2026-02 unified editor
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/projects?published_only=false`, { headers: studioHeaders(), withCredentials: true });
            setProjects(data || []);
        } catch {
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onCreated = () => { setShowForm(false); load(); };

    // 2026-02: "New case study" now creates an empty private draft immediately
    // and opens the unified editor (upload Film + Poster + Gallery, edit text
    // and SEO, reorder, preview, publish all in one place).
    const onNewCaseStudy = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const now = new Date();
            const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${now.getHours()}${now.getMinutes()}${now.getSeconds()}`;
            const payload = {
                title: `Untitled case study ${ymd}`,
                category: "Brand Film",
                hero_image: "/placeholder-case.png",
                gallery: [],
                description: "",
                services: [],
                deliverables: [],
                published: false,
            };
            const { data } = await axios.post(`${API}/projects`, payload, { headers: studioHeaders() });
            setEditorSlug(data.slug);
            await load();
            toast.success("Draft created — edit everything in the full editor");
        } catch (err) {
            toast.error("Create failed", { description: err?.response?.data?.detail || err.message });
        } finally {
            setCreating(false);
        }
    };

    const heroInputRef = React.useRef(null);
    const [heroTarget, setHeroTarget] = useState(null);
    const [cropState, setCropState] = useState(null);      // { file, slug }
    const [galleryFor, setGalleryFor] = useState(null);    // project
    const [videoFor, setVideoFor] = useState(null);        // project — Phase MVP: Hero Film upload

    const onPickHero = (p) => { setHeroTarget(p); setTimeout(() => heroInputRef.current?.click(), 0); };

    const onHeroFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !heroTarget) return;
        setCropState({ file, slug: heroTarget.slug });  // crop/position before upload
    };

    const onCreateFromIg = async () => {
        const url = window.prompt("贴上公开的 Instagram Reel / 帖子链接 — 自动带入标题、日期和封面:", "https://www.instagram.com/");
        if (url == null) return;
        try {
            const { data } = await axios.post(`${API}/projects/from-instagram`, { url }, { headers: studioHeaders() });
            toast.success(`已创建草稿「${data.title}」— 检查资料后点 Publish`);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Autofill failed"); }
    };

    const onFetchIgCover = async (p) => {
        const url = window.prompt("Instagram Reel / 帖子链接 (公开):", p.instagram_url || "https://www.instagram.com/");
        if (url == null) return;
        try {
            await axios.post(`${API}/projects/${p.slug}/hero-from-instagram`, { url }, { headers: studioHeaders() });
            toast.success("已从 Instagram 抓取封面"); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Fetch failed"); }
    };

    const onTogglePublish = async (p) => {
        try {
            await axios.patch(`${API}/projects/${p.slug}`, { published: !p.published }, { headers: studioHeaders() });
            toast.success(p.published ? "Unpublished — hidden from /work" : "Published — now live on /work");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    const onQuickEdit = async (p, field, label) => {
        const val = window.prompt(`${label} for "${p.title}":`, p[field] || "");
        if (val == null) return;
        try {
            await axios.patch(`${API}/projects/${p.slug}`, { [field]: val }, { headers: studioHeaders() });
            toast.success("Saved"); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    };

    const onEditDescription = async (p) => {
        const val = window.prompt(
            `Case study details for "${p.title}" — replace every 【待核实】 with real, client-approved facts (Client / Location / Goal / Deliverables / Results):`,
            p.description || "");
        if (val == null) return;
        try {
            await axios.patch(`${API}/projects/${p.slug}`, { description: val }, { headers: studioHeaders() });
            toast.success("Saved"); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    };

    const onDelete = async (slug) => {
        try {
            await axios.delete(`${API}/projects/${slug}`, { headers: studioHeaders() });
            toast.success("Project deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    return (
        <div>
            {editorSlug ? (
                <FullProjectEditor
                    slug={editorSlug}
                    onBack={() => { setEditorSlug(null); load(); }}
                    onChanged={load}
                />
            ) : (<>
            <input type="file" accept="image/*" ref={heroInputRef} onChange={onHeroFile} className="hidden" data-testid="hero-file-input" />
            {cropState && <CoverCropModal file={cropState.file} slug={cropState.slug} headers={studioHeaders()}
                onDone={() => { setCropState(null); load(); }} onCancel={() => setCropState(null)} />}
            {galleryFor && <GalleryModal project={galleryFor} headers={studioHeaders()}
                onClose={() => setGalleryFor(null)} onChanged={load} />}
            {videoFor && <VideoUploadModal project={videoFor} headers={studioHeaders()}
                onClose={() => setVideoFor(null)} onChanged={load} />}
            <details className="mb-5 text-sm text-cafe-mocha bg-cafe-latte/50 border border-border rounded-2xl px-4 py-3" data-testid="publish-walkthrough">
                <summary className="cursor-pointer select-none font-medium text-cafe-espresso">📋 3 步发布你的第一个案例 · Publish walkthrough</summary>
                <ol className="mt-3 space-y-2 list-none">
                    <li><b>① 填真实资料</b> — 卡片菜单 ⋯ → <em>Edit details</em>,把所有【待核实】换成真实的客户/地点/目标/交付/成果;标题里的【草稿·待核实】也要删掉(<em>Edit title</em>)。</li>
                    <li><b>② 上传封面</b> — 卡片菜单 ⋯ → <em>Upload cover image</em>(从电脑/手机选图,可拖动裁剪),或 <em>Fetch cover from Instagram</em> 贴 Reel 链接自动抓封面;想加更多照片用 <em>Manage gallery</em>。</li>
                    <li><b>③ 点 Publish</b> — 卡片上的绿色 <em>Publish</em> 按钮。发布后立刻出现在 <em>slowcafe.my/work</em>。若被拦截,提示会告诉你还缺什么(资料未核实 / 没封面)。</li>
                </ol>
            </details>
            <div className="flex justify-between items-center mb-6">
                <div className="text-cafe-mocha text-sm">{projects.length} project{projects.length !== 1 ? "s" : ""}</div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={onCreateFromIg} data-testid="create-from-ig-btn"
                        className="rounded-full h-10 px-5 border-cafe-olive text-cafe-olive hover:bg-cafe-olive/10">
                        ⚡ Create from IG link
                    </Button>
                    <Button
                        onClick={onNewCaseStudy}
                        disabled={creating}
                        data-testid="new-project-btn"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5"
                    >
                        <Plus className="mr-2 h-4 w-4" strokeWidth={1.75} />
                        {creating ? "Creating…" : "New case study"}
                    </Button>
                </div>
            </div>

            {showForm && <ProjectForm onCreated={onCreated} /> /* legacy quick-create — kept but no longer triggered by "New case study" (unified editor replaces it) */}

            {loading ? (
                <div className="py-12 text-center text-cafe-mocha">Loading…</div>
            ) : projects.length === 0 ? (
                <div className="py-16 text-center border border-dashed border-border rounded-3xl text-cafe-mocha">
                    No projects yet. Click <em>New case study</em> to add one.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-projects-grid">
                    {projects.map((p) => (
                        <div key={p.id} className="bg-cafe-latte rounded-2xl overflow-hidden border border-border">
                            <Link to={`/work/${p.slug}`} className="block aspect-[4/3] overflow-hidden">
                                {(p.hero_image || "").includes("placeholder") ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-cafe-mocha/60 bg-cafe-cream/60" data-testid={`no-cover-${p.slug}`}>
                                        <span className="text-3xl">□</span>
                                        <span className="mt-2 text-xs">还没有封面 — 用卡片菜单上传或抓取 IG 封面</span>
                                    </div>
                                ) : (
                                    <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover" />
                                )}
                            </Link>
                            <div className="p-5">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cafe-olive">
                                    {categoryLabelFor(p.category, "en") || p.category}
                                    <span className={`normal-case tracking-normal text-[10px] px-2 py-0.5 rounded-full ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                                        data-testid={`project-status-${p.slug}`}>{p.published ? "Live" : "Draft — not public"}</span>
                                </div>
                                <div className="mt-1 font-serif text-xl text-cafe-espresso">{p.title}</div>
                                <div className="mt-1 text-xs text-cafe-mocha">/work/{p.slug}</div>
                                {(p.description || "").includes("待核实") && (
                                    <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid={`verify-warning-${p.slug}`}>
                                        含【待核实】字段 — 请先核实真实资料再发布
                                    </div>
                                )}
                                <div className="mt-4 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => onTogglePublish(p)} data-testid={`toggle-publish-${p.slug}`}
                                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${p.published ? "border-border text-cafe-mocha hover:border-amber-400" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                                            {p.published ? "Unpublish" : "Publish"}
                                        </button>
                                        <Link to={`/work/${p.slug}`} className="text-cafe-olive text-sm hover:underline inline-flex items-center gap-1">
                                            View <ExternalLink className="h-3 w-3" />
                                        </Link>
                                        {!p.published && <DraftPreviewLink project={p} headers={studioHeaders()} />}
                                    </div>
                                    <MoreActions
                                        testid={`more-actions-project-${p.slug}`}
                                        subject={`Case study "${p.title || p.slug}"`}
                                        items={[
                                            { label: "Edit full case study", testid: `edit-full-${p.slug}`, onClick: () => setEditorSlug(p.slug) },
                                            { label: "Upload cover image", testid: `upload-hero-${p.slug}`, onClick: () => onPickHero(p) },
                                            { label: p.video_file_url ? "Hero Film · uploaded ✓ (edit)" : "Upload Hero Film + Poster", testid: `video-upload-${p.slug}`, onClick: () => setVideoFor(p) },
                                            { label: "Fetch cover from Instagram", testid: `ig-hero-${p.slug}`, onClick: () => onFetchIgCover(p) },
                                            { label: `Manage gallery (${(p.gallery || []).length})`, testid: `gallery-${p.slug}`, onClick: () => setGalleryFor(p) },
                                            { label: "Edit details (client/goal/results)", testid: `edit-desc-${p.slug}`, onClick: () => onEditDescription(p) },
                                            { label: "Edit title", testid: `edit-title-${p.slug}`, onClick: () => onQuickEdit(p, "title", "Title") },
                                            { label: "Edit location", testid: `edit-loc-${p.slug}`, onClick: () => onQuickEdit(p, "location", "Location") },
                                            { label: "Edit Instagram URL", testid: `edit-ig-${p.slug}`, onClick: () => onQuickEdit(p, "instagram_url", "Instagram URL") },
                                            {
                                                label: "Delete case study", danger: true, testid: `delete-${p.slug}`,
                                                confirm: "Permanently removes this case study from the public website. This cannot be undone.",
                                                confirmLabel: "Delete permanently",
                                                onClick: () => onDelete(p.slug),
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </>)}
        </div>
    );
}

// ---------- Project form ----------
function ProjectForm({ onCreated }) {
    const [form, setForm] = useState({
        title: "",
        client_name: "",
        category: "Brand Film",
        hero_image: "",
        gallery_input: "",
        description: "",
        shot_date: "",
        location: "",
        services: [],
        instagram_url: "",
    });
    const [saving, setSaving] = useState(false);

    const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const toggleService = (s) =>
        setForm((p) => ({
            ...p,
            services: p.services.includes(s)
                ? p.services.filter((x) => x !== s)
                : [...p.services, s],
        }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            const gallery = form.gallery_input
                .split(/\s*[\n,]\s*/)
                .map((s) => s.trim())
                .filter(Boolean);
            const payload = {
                title: form.title.trim(),
                client_name: form.client_name.trim() || null,
                category: form.category,
                hero_image: form.hero_image.trim() || "/placeholder-case.png",
                gallery,
                description: form.description.trim(),
                shot_date: form.shot_date.trim() || null,
                location: form.location.trim() || null,
                services: form.services,
                instagram_url: form.instagram_url.trim() || null,
                published: false,
            };
            await axios.post(`${API}/projects`, payload, { headers: studioHeaders() });
            toast.success("Case study created as DRAFT — upload a cover, then click Publish on the card");
            onCreated();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error("Save failed", { description: typeof d === "string" ? d : "Please check all required fields" });
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "mt-2 h-11 bg-transparent border border-border rounded-xl px-3 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50";

    return (
        <form
            onSubmit={submit}
            data-testid="project-form"
            className="bg-cafe-latte border border-border rounded-[2rem] p-6 md:p-10 mb-10"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Title *</Label>
                    <Input data-testid="proj-title" value={form.title} onChange={up("title")} placeholder="Opening day at Morning Roast" className={inputCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Client / store name</Label>
                    <Input data-testid="proj-client" value={form.client_name} onChange={up("client_name")} placeholder="Morning Roast" className={inputCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Category *</Label>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                data-testid="proj-category-trigger"
                                className="mt-2 h-11 w-full bg-transparent border border-border rounded-xl px-3 text-left text-cafe-espresso hover:border-cafe-olive"
                            >
                                {form.category}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-cafe-cream border-border rounded-xl w-56">
                            {CATEGORIES.map((c) => (
                                <DropdownMenuItem
                                    key={c}
                                    onClick={() => setForm((p) => ({ ...p, category: c }))}
                                    className="cursor-pointer"
                                >
                                    {c}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Hero image URL (optional)</Label>
                    <Input data-testid="proj-hero" value={form.hero_image} onChange={up("hero_image")} placeholder="留空即可 — 创建后在卡片菜单里「Upload cover image」或「Fetch cover from Instagram」" className={inputCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Gallery URLs (one per line or comma-separated)</Label>
                    <Textarea
                        data-testid="proj-gallery"
                        value={form.gallery_input}
                        onChange={up("gallery_input")}
                        rows={4}
                        placeholder={"https://…\nhttps://…"}
                        className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50"
                    />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Shot date</Label>
                    <Input data-testid="proj-date" value={form.shot_date} onChange={up("shot_date")} placeholder="Nov 2025" className={inputCls} />
                </div>
                <div>
                    <Label className="text-cafe-espresso font-medium">Location</Label>
                    <Input data-testid="proj-location" value={form.location} onChange={up("location")} placeholder="Petaling Jaya" className={inputCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Instagram post URL</Label>
                    <Input data-testid="proj-ig" value={form.instagram_url} onChange={up("instagram_url")} placeholder="https://www.instagram.com/p/…/" className={inputCls} />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Services provided</Label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="proj-services">
                        {SERVICE_OPTIONS.map((s) => {
                            const on = form.services.includes(s);
                            return (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => toggleService(s)}
                                    className={cn(
                                        "h-9 px-4 rounded-full border text-sm transition-all",
                                        on ? "bg-cafe-olive text-cafe-cream border-cafe-olive" : "border-border text-cafe-espresso hover:border-cafe-olive"
                                    )}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="md:col-span-2">
                    <Label className="text-cafe-espresso font-medium">Story / description</Label>
                    <Textarea
                        data-testid="proj-description"
                        value={form.description}
                        onChange={up("description")}
                        rows={5}
                        placeholder="What were you going for? What felt special on shoot day?"
                        className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50"
                    />
                </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
                <Button
                    type="submit"
                    disabled={saving}
                    data-testid="proj-submit"
                    className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8"
                >
                    <ImagePlus className="mr-2 h-4 w-4" strokeWidth={1.75} />
                    {saving ? "Publishing…" : "Publish case study"}
                </Button>
            </div>
        </form>
    );
}

// ---------- Articles tab ----------
function ArticlesTab() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        title: "", excerpt: "", cover_image: "", content: "", author: "Kaily", tags_input: "",
    });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/articles?published_only=false`);
            setArticles(data || []);
        } catch {
            setArticles([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.cover_image.trim() || !form.content.trim()) {
            toast.error("Title, cover image and content are required");
            return;
        }
        setSaving(true);
        try {
            const tags = form.tags_input.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
            await axios.post(`${API}/articles`, {
                title: form.title.trim(),
                excerpt: form.excerpt.trim(),
                cover_image: form.cover_image.trim(),
                content: form.content.trim(),
                author: form.author.trim() || "Kaily",
                tags,
                published: true,
            }, { headers: studioHeaders() });
            toast.success("Article published!");
            setForm({ title: "", excerpt: "", cover_image: "", content: "", author: "Kaily", tags_input: "" });
            setShowForm(false);
            load();
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (slug) => {
        try {
            await axios.delete(`${API}/articles/${slug}`, { headers: studioHeaders() });
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const inputCls = "mt-2 h-11 bg-transparent border border-border rounded-xl px-3 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50";

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div className="text-cafe-mocha text-sm">{articles.length} article{articles.length !== 1 ? "s" : ""}</div>
                <Button
                    onClick={() => setShowForm((v) => !v)}
                    data-testid="new-article-btn"
                    className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5"
                >
                    <Plus className="mr-2 h-4 w-4" strokeWidth={1.75} />
                    {showForm ? "Close" : "New article"}
                </Button>
            </div>

            {showForm && (
                <form
                    onSubmit={submit}
                    data-testid="article-form"
                    className="bg-cafe-latte border border-border rounded-[2rem] p-6 md:p-10 mb-10"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">Title *</Label>
                            <Input data-testid="art-title" value={form.title} onChange={up("title")} className={inputCls} placeholder="5 things every cafe should film themselves" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">Cover image URL *</Label>
                            <Input data-testid="art-cover" value={form.cover_image} onChange={up("cover_image")} className={inputCls} placeholder="https://…" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">Excerpt</Label>
                            <Textarea data-testid="art-excerpt" value={form.excerpt} onChange={up("excerpt")} rows={2} placeholder="Short summary shown on the journal list" className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50" />
                        </div>
                        <div>
                            <Label className="text-cafe-espresso font-medium">Author</Label>
                            <Input data-testid="art-author" value={form.author} onChange={up("author")} className={inputCls} />
                        </div>
                        <div>
                            <Label className="text-cafe-espresso font-medium">Tags (comma-separated)</Label>
                            <Input data-testid="art-tags" value={form.tags_input} onChange={up("tags_input")} className={inputCls} placeholder="cafe tips, behind the scenes" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-cafe-espresso font-medium">Content *</Label>
                            <Textarea data-testid="art-content" value={form.content} onChange={up("content")} rows={10} placeholder="Write your article — plain text or simple markdown" className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50 font-serif text-base leading-relaxed" />
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <Button type="submit" disabled={saving} data-testid="art-submit" className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8">
                            {saving ? "Publishing…" : "Publish"}
                        </Button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="py-12 text-center text-cafe-mocha">Loading…</div>
            ) : articles.length === 0 ? (
                <div className="py-16 text-center border border-dashed border-border rounded-3xl text-cafe-mocha">
                    No articles yet. Write your first one to start ranking on Google.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {articles.map((a) => (
                        <div key={a.id} className="bg-cafe-latte rounded-2xl overflow-hidden border border-border">
                            <Link to={`/journal/${a.slug}`} className="block aspect-[4/3] overflow-hidden">
                                <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                            </Link>
                            <div className="p-5">
                                <div className="font-serif text-xl text-cafe-espresso">{a.title}</div>
                                <div className="mt-1 text-xs text-cafe-mocha">/journal/{a.slug}</div>
                                <div className="mt-4 flex items-center justify-between">
                                    <Link to={`/journal/${a.slug}`} className="text-cafe-olive text-sm hover:underline inline-flex items-center gap-1">
                                        View <ExternalLink className="h-3 w-3" />
                                    </Link>
                                    <MoreActions
                                        testid={`more-actions-article-${a.slug}`}
                                        subject={`Journal post "${a.title || a.slug}"`}
                                        items={[{
                                            label: "Delete post", danger: true, testid: `delete-art-${a.slug}`,
                                            confirm: "Permanently removes this journal post from the public website. This cannot be undone.",
                                            confirmLabel: "Delete permanently",
                                            onClick: () => onDelete(a.slug),
                                        }]}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------- Inquiries tab ----------
function InquiriesTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/inquiries`, { headers: studioHeaders() });
                setRows(data || []);
            } catch {
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className="py-12 text-center text-cafe-mocha">Loading inquiries…</div>;

    if (rows.length === 0) {
        return (
            <div className="py-16 text-center border border-dashed border-border rounded-3xl text-cafe-mocha">
                No inquiries yet.
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="admin-inquiries">
            {rows.map((r) => (
                <div key={r.id} className="bg-cafe-latte border border-border rounded-2xl p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="font-serif text-xl text-cafe-espresso">{r.name}{r.business_name && ` · ${r.business_name}`}</div>
                            <div className="mt-1 text-sm text-cafe-mocha">
                                <a href={`mailto:${r.email}`} className="hover:text-cafe-olive">{r.email}</a>
                                {r.phone && <span> · {r.phone}</span>}
                            </div>
                        </div>
                        <div className="text-xs text-cafe-mocha">{new Date(r.created_at).toLocaleString()}</div>
                    </div>
                    {r.services && r.services.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {r.services.map((s) => (
                                <span key={s} className="text-xs px-3 py-1 rounded-full bg-cafe-cream border border-border text-cafe-mocha">{s}</span>
                            ))}
                        </div>
                    )}
                    {r.message && <p className="mt-4 text-cafe-espresso leading-relaxed whitespace-pre-wrap">{r.message}</p>}
                    {(r.budget || r.timeline) && (
                        <div className="mt-3 text-sm text-cafe-mocha">
                            {r.budget && <span>Budget: {r.budget}</span>}
                            {r.budget && r.timeline && <span> · </span>}
                            {r.timeline && <span>Timeline: {r.timeline}</span>}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ---------- Instagram tab (MOCKED) ----------
function InstagramTab() {
    const [caption, setCaption] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [scheduledFor, setScheduledFor] = useState("");
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/instagram/post`, { headers: studioHeaders() });
            setQueue(data || []);
        } catch {
            setQueue([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!caption.trim() || !imageUrl.trim()) {
            toast.error("Caption and image URL are required");
            return;
        }
        setSaving(true);
        try {
            await axios.post(
                `${API}/instagram/post`,
                {
                    caption: caption.trim(),
                    image_url: imageUrl.trim(),
                    scheduled_for: scheduledFor || null,
                },
                { headers: studioHeaders() }
            );
            toast.success("Post queued (MOCKED)", {
                description: "This is stored in the DB. Real publishing goes live once Meta Graph API is connected.",
            });
            setCaption(""); setImageUrl(""); setScheduledFor("");
            load();
        } catch {
            toast.error("Failed to queue post");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <form
                onSubmit={submit}
                data-testid="ig-post-form"
                className="lg:col-span-3 bg-cafe-latte border border-border rounded-[2rem] p-6 md:p-8"
            >
                <div className="flex items-start gap-3 mb-6 p-4 bg-cafe-cream border border-cafe-terracotta/40 rounded-xl text-sm text-cafe-mocha">
                    <AlertTriangle className="h-4 w-4 text-cafe-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                        <strong className="text-cafe-espresso">MOCKED — not actually posting yet.</strong>
                        {" "}Posts are saved to the queue. To go live, connect Meta Graph API (see README).
                    </div>
                </div>

                <Label className="text-cafe-espresso font-medium">Caption *</Label>
                <Textarea
                    data-testid="ig-caption"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    rows={5}
                    placeholder="Write your IG caption… (max 2200 chars)"
                    className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50"
                />

                <div className="mt-4">
                    <Label className="text-cafe-espresso font-medium">Image URL *</Label>
                    <Input
                        data-testid="ig-image-url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://…"
                        className="mt-2 h-11 bg-transparent border border-border rounded-xl px-3 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50"
                    />
                </div>

                <div className="mt-4">
                    <Label className="text-cafe-espresso font-medium">Schedule (optional)</Label>
                    <Input
                        data-testid="ig-schedule"
                        type="datetime-local"
                        value={scheduledFor}
                        onChange={(e) => setScheduledFor(e.target.value)}
                        className="mt-2 h-11 bg-transparent border border-border rounded-xl px-3 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso"
                    />
                </div>

                <Button
                    type="submit"
                    disabled={saving}
                    data-testid="ig-submit"
                    className="mt-8 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8"
                >
                    <Film className="mr-2 h-4 w-4" strokeWidth={1.75} />
                    {saving ? "Queuing…" : "Queue post"}
                </Button>
            </form>

            <div className="lg:col-span-2">
                <div className="text-xs uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4 flex items-center gap-2">
                    <Clock3 className="h-3.5 w-3.5" strokeWidth={1.75} /> Queue
                </div>
                {loading ? (
                    <div className="text-cafe-mocha text-sm">Loading…</div>
                ) : queue.length === 0 ? (
                    <div className="text-sm text-cafe-mocha">No queued posts yet.</div>
                ) : (
                    <ul className="flex flex-col gap-3" data-testid="ig-queue">
                        {queue.map((q) => (
                            <li key={q.id} className="bg-cafe-latte border border-border rounded-xl p-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-lg overflow-hidden bg-cafe-sand flex-shrink-0">
                                        <img src={q.image_url} alt="preview" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-cafe-espresso line-clamp-2">{q.caption}</div>
                                        <div className="text-xs text-cafe-mocha mt-1">
                                            {q.scheduled_for ? `Scheduled · ${new Date(q.scheduled_for).toLocaleString()}` : "No schedule"}
                                            {q.mocked && <span className="ml-2 px-2 py-0.5 rounded-full bg-cafe-terracotta/20 text-cafe-terracotta text-[10px] uppercase tracking-wider">Mocked</span>}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}


// Read-only Archive — legacy AI pipeline, website inquiries and legacy bookings are preserved, not edited.
function ArchiveTab() {
    const [view, setView] = React.useState("bookings");
    const V = [["bookings", "Legacy Bookings"], ["inquiries", "Website Inquiries"], ["sales-brain", "Legacy AI Pipeline"]];
    return (
        <div data-testid="archive-tab">
            <div className="rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-5 py-3 text-sm mb-4" data-testid="archive-banner">
                <b>Archive · read-only.</b> These legacy records are preserved for reference. Daily work happens in Leads &amp; Sales and Client Projects — website enquiries now flow into the Sales CRM automatically.
            </div>
            <div className="flex gap-1.5 mb-4">
                {V.map(([k, l]) => <button key={k} type="button" onClick={() => setView(k)} data-testid={`archive-view-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${view === k ? "bg-cafe-espresso text-cafe-cream border-cafe-espresso" : "bg-white border-neutral-200"}`}>{l}</button>)}
            </div>
            <div className="pointer-events-none select-text opacity-90" aria-readonly="true" data-testid="archive-readonly">
                {view === "bookings" && <ShootOrdersTab />}
                {view === "inquiries" && <InquiriesTab />}
                {view === "sales-brain" && <SalesBrainTab />}
            </div>
        </div>
    );
}
